/**
 * The matcher-v2 dashboard section: today's recommendations, the check
 * shelf, and the five honest result states — `GET /api/matches/decisions`
 * (PIVOT-301, PIVOT-373). Sits alongside `Dashboard.tsx`'s legacy
 * `GET /matches` grid (`MatchCard`); the two never render together, because
 * `contract` on the wire says which one this account is on.
 *
 * **PIVOT-373: the contract picks the screen, and it is read, not guessed.**
 * This component used to take a `pendingAlone` prop, because a v1 account's
 * decisions body was indistinguishable from a v2 candidate mid-cold-start —
 * both empty, both `pending`. `Dashboard.tsx` therefore passed `false` and
 * suppressed a real state to avoid a false one. The account's own contract
 * now rides on the response, so the ambiguity is gone and so is the prop: a
 * v1 account renders nothing here, and a v2 account gets the true state.
 *
 * **The five states, each its own carried fact** (never derived from an
 * empty list — CLAUDE.md: carry a fact across a boundary):
 *
 * | fact | what it says |
 * | --- | --- |
 * | `budget_delayed` | today's reading budget is spent; the rest waits |
 * | `pending` | funded work is still in flight |
 * | `policy_approval_needed` | a result is held until it faces a new rule |
 * | `information_insufficient` | the postings we reached could not be read |
 * | `dry_day` | the pool finished and recommended nothing |
 *
 * They are not mutually exclusive on the wire, so this file picks ONE notice
 * in the order above: the first three each mean "the day is not finished",
 * and saying that beats saying anything about what did finish. A dry day is
 * last because it is the only one of the five that is a completed answer.
 *
 * A dry day and a non-empty shelf now coexist (PIVOT-373 A4): the shelf holds
 * questions, never recommendations, so "nothing recommended today" is true
 * beside it and the shelf still renders under its own heading. Neither pads
 * the other.
 */

import { MatcherV2Card } from './MatcherV2Card'
import { useI18n } from '../i18n'
import type { MatcherV2Decisions } from '../types/domain'
import type { TranslationKey } from '../i18n/types'
import './MatcherV2Section.css'

interface MatcherV2SectionProps {
  decisions: MatcherV2Decisions | null
  onStale: (jobId: string) => void
}

export function MatcherV2Section({ decisions, onStale }: MatcherV2SectionProps) {
  const { t } = useI18n()

  if (!decisions) return null
  // A legacy-scorer account has no matcher-v2 surface at all. Read from the
  // account's contract, never from the lists being empty.
  if (decisions.contract !== 'v2') return null

  const hasContent = decisions.recommended.length > 0 || decisions.shelf.length > 0

  // One notice, chosen in the documented precedence order above. `null` means
  // the day finished with something to show and nothing to explain.
  const notice: { title: TranslationKey; body: TranslationKey } | null =
    decisions.budget_delayed
    ? { title: 'matcherV2.budgetDelayTitle', body: 'matcherV2.budgetDelayBody' }
    : decisions.pending && !hasContent
      ? { title: 'matcherV2.pendingTitle', body: 'matcherV2.pendingBody' }
      : decisions.policy_approval_needed
        ? { title: 'matcherV2.policyHoldTitle', body: 'matcherV2.policyHoldBody' }
        : decisions.information_insufficient
          ? {
              title: 'matcherV2.insufficientStateTitle',
              body: 'matcherV2.insufficientStateBody',
            }
          : decisions.dry_day
            ? { title: 'matcherV2.dryDayTitle', body: 'matcherV2.dryDayBody' }
            : null

  if (!notice && !hasContent) return null

  return (
    <div className="matcher-v2-section">
      {notice && (
        <div className="matcher-v2-section-notice">
          <h2>{t(notice.title)}</h2>
          <p>{t(notice.body)}</p>
        </div>
      )}
      {/* Work still in flight ALONGSIDE real cards: a small qualifier, not a
          state of its own — the cards below are already true. */}
      {decisions.pending && hasContent && (
        <p className="matcher-v2-section-pending-note">{t('matcherV2.morePending')}</p>
      )}
      {decisions.recommended.length > 0 && (
        <section>
          <h2>{t('matcherV2.recommendedTitle')}</h2>
          <div className="matcher-v2-grid">
            {decisions.recommended.map((jobId) => {
              const facts = decisions.facts[jobId]
              return facts ? (
                <MatcherV2Card
                  key={jobId}
                  facts={facts}
                  // PIVOT-371: the whole list was read at ONE evidence
                  // identity, so every card echoes that one back. A card that
                  // carried its own copy could send a stale one after a
                  // sibling's answer moved it.
                  candidateEvidenceSha={decisions.candidate_evidence_sha}
                  onStale={onStale}
                />
              ) : null
            })}
          </div>
        </section>
      )}
      {decisions.shelf.length > 0 && (
        <section>
          <h2>{t('matcherV2.shelfTitle')}</h2>
          <p className="matcher-v2-section-shelf-body">{t('matcherV2.shelfBody')}</p>
          <div className="matcher-v2-grid">
            {decisions.shelf.map((jobId) => {
              const facts = decisions.facts[jobId]
              return facts ? (
                <MatcherV2Card
                  key={jobId}
                  facts={facts}
                  candidateEvidenceSha={decisions.candidate_evidence_sha}
                  onStale={onStale}
                />
              ) : null
            })}
          </div>
        </section>
      )}
    </div>
  )
}
